/* global google */
import { useJsApiLoader } from "@react-google-maps/api";
import { debounce } from "lodash";
import { FieldValues, Path, PathValue, useFormContext } from "react-hook-form";
import { toast } from "react-toastify";

export function useResolveGeoCoordinates<T extends FieldValues>(
  streetField: Path<T>,
  zipField: Path<T>,
  cityField: Path<T>,
  latitudeField: Path<T>,
  longitudeField: Path<T>
) {
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GMAPS_API_KEY ?? "",
  });

  const { getValues, setValue } = useFormContext<T>();

  const resolveGeoCoordinatesIfAddressIsComplete = debounce(async () => {
    if (!isLoaded) {
      toast.error("GMaps Geocoding service not loaded - address could not be resolved");
    }

    const values = getValues();
    const street = values[streetField];
    const zipCode = values[zipField];
    const city = values[cityField];

    if (addressIsIncomplete(street, zipCode, city)) return;
    if (values[latitudeField] || values[longitudeField]) return;

    const geoCoder = new google.maps.Geocoder();
    geoCoder
      .geocode(
        {
          address: `${street}, ${zipCode} ${city}`,
        },
        (resolvedLocations) => {
          if (!resolvedLocations || resolvedLocations?.length < 0) return;
          const location = resolvedLocations?.[0].geometry.location;
          setValue(latitudeField, location?.lat() as PathValue<T, Path<T>>);
          setValue(longitudeField, location?.lng() as PathValue<T, Path<T>>);
        }
      )
      .catch(() =>
        toast.error(
          "The geolocation for the specified address could not be resolved. " +
            "Please check if the address exists."
        )
      );
  }, 1000);

  return {
    resolveGeoCoordinatesIfAddressIsComplete,
  };
}

function addressIsIncomplete(street?: string, zipCode?: string, city?: string) {
  return !street?.trim() || !zipCode?.trim() || !city?.trim();
}
